import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { logout, setAccessToken } from 'store/authSlice';
import { RootState } from 'store/store';

const mutex = new Mutex();
const baseUrl = process.env.REACT_APP_API_URL;
const AUTH_ENDPOINTS_WITHOUT_REAUTH = [
  '/auth/login',
  '/auth/refresh-token',
  '/auth/logout',
  '/auth/register',
  '/auth/signup',
  '/auth/confirm-email',
  '/auth/forgot-password',
  '/auth/reset-password'
];

const isUnauthorizedError = (serverError): boolean => {
  return serverError?.data === 'Unauthorized' && serverError?.originalStatus === 401;
};

const getRequestUrl = (args: string | { url?: string }): string => {
  if (typeof args === 'string') {
    return args;
  }

  return args?.url || '';
};

export const shouldSkipReAuth = (requestUrl: string): boolean => {
  if (!requestUrl) {
    return false;
  }

  return AUTH_ENDPOINTS_WITHOUT_REAUTH.some((path) => requestUrl.startsWith(path));
};

const baseQuery = fetchBaseQuery({
  baseUrl,
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const accessToken = (getState() as RootState).auth.accessToken;
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return headers;
  }
});

const publicBaseQuery = fetchBaseQuery({
  baseUrl
});

export const baseQueryWithReAuth = async (args, api, extraOptions) => {
  if (extraOptions?.skipReAuth) {
    return publicBaseQuery(args, api, extraOptions);
  }

  const requestUrl = getRequestUrl(args);
  if (shouldSkipReAuth(requestUrl)) {
    return baseQuery(args, api, extraOptions);
  }

  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (isUnauthorizedError(result.error)) {
    if (shouldSkipReAuth(requestUrl)) {
      return result;
    }

    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        // try to get a new access token
        const refreshResult = await baseQuery(
          {
            url: '/auth/refresh-token',
            method: 'POST'
          },
          api,
          extraOptions
        );
        const response: any = refreshResult.data;
        const accessToken: string = response?.data?.accessToken;
        if (accessToken) {
          api.dispatch(setAccessToken(accessToken));
          result = await baseQuery(args, api, extraOptions);
        } else {
          // Central teardown is attached to `logout` listeners in store middleware.
          const state = api.getState() as RootState;
          if (state.auth.accessToken) {
            api.dispatch(logout());
          }
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      const state = api.getState() as RootState;
      if (!state.auth.accessToken) {
        return result;
      }
      // retry the initial query
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};
